<template>
  <div class="neuro-card" ref="cardRef" @mousemove="handleMouseMove" @mouseleave="handleMouseLeave">
    <!-- Neural network canvas - Interactive particles -->
    <canvas ref="canvasRef" class="neuro-canvas"></canvas>

    <!-- Main content -->
    <div class="neuro-card__content" :class="{ 'is-revealed': isRevealed }">
      <!-- Hidden state - Teaser -->
      <div class="neuro-card__teaser" v-if="!isRevealed">
        <p class="teaser__text" v-html="$t('page.about.neurodivergentCard.teaser')"></p>
        <MagicButton
          variant="secondary"
          size="md"
          :text="$t('page.about.neurodivergentCard.button')"
          @click="revealSuperpowers"
        >
        </MagicButton>
      </div>

      <!-- Revealed state - Full content -->
      <div class="neuro-card__revealed" v-else>
        <div class="neuro-card__header">
          <div class="neuro-card__title-group">
            <span class="neuro-card__label">{{ $t('page.about.neurodivergentCard.label') }}</span>
          </div>
          <button
            class="neuro-card__toggle"
            @click="hideSuperpowers"
            :title="$t('page.about.neurodivergentCard.hide')"
          >
            <ViewOffIcon class="toggle-icon" />
          </button>
        </div>

        <div class="neuro-card__badges">
          <span class="neuro-badge neuro-badge--autism">
            <span class="infinity-symbol">∞</span>
            <span class="badge-text">{{ $t('page.about.neurodivergentCard.badges.autism') }}</span>
          </span>
          <span class="neuro-badge neuro-badge--gifted">
            <span class="star-symbol">✦</span>
            <span class="badge-text">{{
              $t('page.about.neurodivergentCard.badges.giftedness')
            }}</span>
          </span>
        </div>

        <!-- 
        
        Português (laudo): Altas Habilidades / Superdotação

        Español (clínico): Altas Capacidades / Superdotación

        English (clínico): High Intellectual Potential (Giftedness)
        -->

        <!-- Superpowers grid 2x2 -->
        <div class="neuro-card__powers-grid">
          <div
            v-for="(power, index) in superpowers"
            :key="power.id"
            class="power-box"
            :style="{ animationDelay: index * 0.1 + 's' }"
          >
            <span class="power-box__name">{{ power.name }}</span>
          </div>
        </div>

        <!-- Footer quote -->
        <div class="neuro-card__footer">
          <p class="footer-quote" v-html="$t('page.about.neurodivergentCard.footer')"></p>
        </div>
      </div>

      <!-- Reveal animation overlay -->
      <div class="reveal-pulse" :class="{ 'is-active': showPulse }"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, reactive, computed } from 'vue'
import MagicButton from '@/components/MagicButton.vue'
import { ViewOffIcon } from 'hugeicons-vue'
import { useI18n } from 'vue-i18n'

const cardRef = ref(null)
const canvasRef = ref(null)

// Mouse state
const mouse = reactive({ x: null, y: null, radius: 100 })

// Reveal state
const isRevealed = ref(false)
const showPulse = ref(false)

const revealSuperpowers = () => {
  showPulse.value = true
  setTimeout(() => {
    isRevealed.value = true
    showPulse.value = false
  }, 600)
}

const hideSuperpowers = () => {
  isRevealed.value = false
}

// Superpowers data - 4 core abilities
const { tm } = useI18n()
const superpowers = computed(() => tm('page.about.neurodivergentCard.superpowers'))

// Synapse waves - curved flowing lines positioned at corners
const synapseWaves = reactive([
  // Top-left corner waves
  { path: 'M -10,5 Q 20,15 50,8 T 90,20', active: false, delay: 0 },
  { path: 'M 5,-5 Q 15,25 40,15 T 70,30', active: false, delay: 0.2 },
  { path: 'M -5,25 Q 25,10 55,30 T 85,15', active: false, delay: 0.4 },
  // Top-right corner waves
  { path: 'M 120,5 Q 150,20 180,10 T 210,25', active: false, delay: 0.1 },
  { path: 'M 130,-5 Q 160,15 190,5 T 215,15', active: false, delay: 0.3 },
  // Bottom-left corner waves
  { path: 'M -10,270 Q 20,285 50,275 T 90,290', active: false, delay: 0.15 },
  { path: 'M 5,285 Q 30,270 60,290 T 85,275', active: false, delay: 0.35 },
  // Bottom-right corner waves
  { path: 'M 120,275 Q 155,290 185,280 T 215,295', active: false, delay: 0.25 },
  { path: 'M 130,290 Q 165,275 195,295 T 220,280', active: false, delay: 0.45 },
])

let flashInterval = null
const triggerFlashes = () => {
  flashInterval = setInterval(() => {
    // Flash 3-5 random waves
    const numToFlash = 3 + Math.floor(Math.random() * 3)

    // Reset all
    synapseWaves.forEach((w) => (w.active = false))

    // Activate random ones
    const indices = []
    while (indices.length < numToFlash) {
      const idx = Math.floor(Math.random() * synapseWaves.length)
      if (!indices.includes(idx)) {
        indices.push(idx)
        synapseWaves[idx].active = true
      }
    }
  }, 2000)
}

// Mouse handlers
const handleMouseMove = (event) => {
  const rect = cardRef.value.getBoundingClientRect()
  mouse.x = event.clientX - rect.left
  mouse.y = event.clientY - rect.top
}

const handleMouseLeave = () => {
  mouse.x = null
  mouse.y = null
}

// Particle system
let particles = []
let animationId = null
let ctx = null

class Particle {
  constructor(x, y, canvasWidth, canvasHeight) {
    this.x = x
    this.y = y
    this.size = Math.random() * 2 + 0.5
    this.baseX = x
    this.baseY = y
    this.density = Math.random() * 15 + 5
    this.angle = Math.random() * Math.PI * 2
    this.canvasWidth = canvasWidth
    this.canvasHeight = canvasHeight
  }

  draw() {
    ctx.beginPath()
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(139, 92, 246, 0.8)'
    ctx.fill()
  }

  update() {
    // Gentle floating movement
    this.angle += 0.01
    const floatX = Math.cos(this.angle) * 0.3
    const floatY = Math.sin(this.angle) * 0.3

    if (mouse.x !== null && mouse.y !== null) {
      const dx = mouse.x - this.x
      const dy = mouse.y - this.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < mouse.radius) {
        const forceDirectionX = dx / distance
        const forceDirectionY = dy / distance
        const force = (mouse.radius - distance) / mouse.radius
        const directionX = forceDirectionX * force * this.density
        const directionY = forceDirectionY * force * this.density

        this.x -= directionX
        this.y -= directionY
      } else {
        // Return to base position smoothly
        if (this.x !== this.baseX) {
          const dx = this.x - this.baseX
          this.x -= dx / 20
        }
        if (this.y !== this.baseY) {
          const dy = this.y - this.baseY
          this.y -= dy / 20
        }
        this.x += floatX
        this.y += floatY
      }
    } else {
      // Just float when no mouse
      if (this.x !== this.baseX) {
        const dx = this.x - this.baseX
        this.x -= dx / 30
      }
      if (this.y !== this.baseY) {
        const dy = this.y - this.baseY
        this.y -= dy / 30
      }
      this.x += floatX
      this.y += floatY
    }

    this.draw()
  }
}

const initParticles = () => {
  if (!canvasRef.value || !cardRef.value) return

  const canvas = canvasRef.value
  canvas.width = cardRef.value.offsetWidth
  canvas.height = cardRef.value.offsetHeight
  ctx = canvas.getContext('2d')

  particles = []
  const numberOfParticles = 40

  for (let i = 0; i < numberOfParticles; i++) {
    const x = Math.random() * canvas.width
    const y = Math.random() * canvas.height
    particles.push(new Particle(x, y, canvas.width, canvas.height))
  }
}

const connectParticles = () => {
  const canvas = canvasRef.value
  if (!canvas) return

  for (let a = 0; a < particles.length; a++) {
    for (let b = a + 1; b < particles.length; b++) {
      const dx = particles[a].x - particles[b].x
      const dy = particles[a].y - particles[b].y
      const distance = Math.sqrt(dx * dx + dy * dy)

      const maxDistance = canvas.width / 4

      if (distance < maxDistance) {
        const opacity = (1 - distance / maxDistance) * 0.3
        ctx.strokeStyle = `rgba(139, 92, 246, ${opacity})`
        ctx.lineWidth = 0.5
        ctx.beginPath()
        ctx.moveTo(particles[a].x, particles[a].y)
        ctx.lineTo(particles[b].x, particles[b].y)
        ctx.stroke()
      }
    }
  }
}

const animate = () => {
  if (!ctx || !canvasRef.value) return

  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)

  for (const particle of particles) {
    particle.update()
  }

  connectParticles()
  animationId = requestAnimationFrame(animate)
}

// Thought patterns (removed - now using superpowers grid)
let patternInterval = null

// Intersection Observer
let observer = null
let animationStarted = false

const setupObserver = () => {
  if (!cardRef.value) return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !animationStarted) {
          animationStarted = true
          initParticles()
          animate()
          triggerFlashes()
        }
      })
    },
    { threshold: 0.3 }
  )

  observer.observe(cardRef.value)
}

// Resize handler
const handleResize = () => {
  if (animationStarted) {
    initParticles()
  }
}

onMounted(() => {
  setupObserver()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
  if (animationId) cancelAnimationFrame(animationId)
  if (patternInterval) clearInterval(patternInterval)
  if (flashInterval) clearInterval(flashInterval)
  window.removeEventListener('resize', handleResize)
})
</script>

<style>
.neuro-card {
  position: relative;
  height: 100%;
  padding: 1rem;
  overflow: hidden;
}

/* Neural canvas - Interactive particles */
.neuro-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.6;
  z-index: 0;
  pointer-events: none;
}

/* Content */
.neuro-card__content {
  position: relative;
  z-index: 10;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* ========== TEASER STATE ========== */
.neuro-card__teaser {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  animation: teaser-in 0.5s ease-out;
}

@keyframes teaser-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.teaser__text {
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 1.25rem;
}

.teaser__highlight {
  background: linear-gradient(90deg, var(--color-electric-purple), #818cf8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
}

.teaser__infinity {
  font-size: 1.25rem;
  color: #f59e0b;
}

/* ========== REVEALED STATE ========== */
.neuro-card__revealed {
  display: flex;
  flex-direction: column;
  height: 100%;
  animation: reveal-in 0.6s ease-out;
}

@keyframes reveal-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.neuro-card__header {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
}

.neuro-card__title-group {
  display: flex;
  flex-direction: column;
}

.neuro-card__label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-muted);
  margin-bottom: 0.125rem;
}

.neuro-card__title {
  font-size: 1rem;
  font-weight: 700;
  background: linear-gradient(90deg, var(--color-electric-purple), #818cf8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
}

.neuro-card__toggle {
  position: absolute;
  top: 0;
  right: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.3s ease;
  z-index: 10;
}

.neuro-card__toggle:hover {
  opacity: 1;
  background: var(--color-electric-purple);
  border-color: var(--color-electric-purple);
}

.toggle-icon {
  width: 1rem;
  height: 1rem;
  color: var(--color-text-secondary);
}

.neuro-card__toggle:hover .toggle-icon {
  color: white;
}

/* Badges */
.neuro-card__badges {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.neuro-badge {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
}

.neuro-badge:hover {
  border-color: var(--color-electric-purple);
}

.neuro-badge--autism .infinity-symbol {
  color: #f59e0b;
  font-size: 0.75rem;
  font-weight: bold;
}

.neuro-badge--gifted .star-symbol {
  color: var(--color-electric-purple);
  font-size: 0.625rem;
}

.badge-text {
  color: var(--color-text-secondary);
}

/* Superpowers Grid 2x2 */
.neuro-card__powers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  flex: 1;
}

.power-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  background: rgba(139, 92, 246, 0.06);
  border-radius: 0.625rem;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  animation: power-slide-in 0.4s ease-out backwards;
}

@keyframes power-slide-in {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.power-box__name {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
}

/* Footer Quote */
.neuro-card__footer {
  margin-top: auto;
  padding-top: 0.625rem;
}

.footer-quote {
  @apply text-sm text-center;
  color: var(--color-text-secondary);
  margin: 0;
  font-style: italic;
}

.footer-highlight {
  color: var(--color-electric-purple);
  font-weight: 600;
  font-style: normal;
}

/* Reveal Pulse Animation */
.reveal-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.5) 0%, transparent 70%);
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 5;
}

.reveal-pulse.is-active {
  animation: pulse-expand 0.6s ease-out forwards;
}

@keyframes pulse-expand {
  0% {
    width: 0;
    height: 0;
    opacity: 1;
  }
  100% {
    width: 400%;
    height: 400%;
    opacity: 0;
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .neuro-card {
    padding: 1rem;
  }

  .teaser__text {
    font-size: 0.875rem;
  }

  .teaser__button {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }

  .neuro-card__badges {
    flex-wrap: wrap;
  }

  .power-item {
    padding: 0.375rem 0.5rem;
  }

  .power-name {
    font-size: 0.6875rem;
  }

  .power-desc {
    font-size: 0.5625rem;
  }
}
</style>
